import { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjectId } from '@client/store/project/project.hook';
import {
  DESIGN_CAMERA_SEARCH_PARAM,
  DESIGN_SELECTED_SEARCH_PARAM,
  DESIGN_STOREY_SEARCH_PARAM,
  parseDesignCamera,
  parseDesignSelectionUrlState,
  serializeDesignCamera,
} from '../helpers/design-url-state.helpers';
import {
  ensureDesignSessionForProject,
  useDesignStore,
} from '../store/design.store';
import {
  useDesignMapCamera,
  useIsBuildingOutlineSelected,
  useSelectedBuildingId,
  useSelectedStoreyId,
} from '../store/design-store.hooks';

/**
 * Keep shareable Design selection and camera state in the query string.
 * Replacements avoid adding one browser-history entry per camera movement.
 */
export const useDesignUrlState = (): void => {
  const projectId = useProjectId();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBuildingId = useSelectedBuildingId();
  const selectedStoreyId = useSelectedStoreyId();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const camera = useDesignMapCamera();
  const [hydratedProjectId, setHydratedProjectId] = useState<
    number | undefined
  >(undefined);

  const selectedParam = searchParams.get(DESIGN_SELECTED_SEARCH_PARAM);
  const storeyParam = searchParams.get(DESIGN_STOREY_SEARCH_PARAM);
  const cameraParam = searchParams.get(DESIGN_CAMERA_SEARCH_PARAM);

  useLayoutEffect(() => {
    ensureDesignSessionForProject(projectId);
    const selection = parseDesignSelectionUrlState(selectedParam, storeyParam);
    const parsedCamera = parseDesignCamera(cameraParam);
    const current = useDesignStore.getState();
    if (
      current.selectedBuildingId !== selection.selectedBuildingId ||
      current.selectedStoreyId !== selection.selectedStoreyId ||
      current.isBuildingOutlineSelected !==
        selection.isBuildingOutlineSelected ||
      serializeDesignCamera(current.camera) !==
        serializeDesignCamera(parsedCamera)
    ) {
      useDesignStore.setState({ ...selection, camera: parsedCamera });
    }
    setHydratedProjectId(projectId);
  }, [cameraParam, projectId, selectedParam, storeyParam]);

  useEffect(() => {
    if (hydratedProjectId !== projectId) return;
    // URL hydration runs in a layout effect and can change the store after
    // this render. Read its current state so an older render cannot write the
    // previous floor/camera back to the URL and start a feedback loop.
    const current = useDesignStore.getState();
    const nextSelected = current.selectedBuildingId || null;
    const nextStorey =
      current.selectedStoreyId && !current.isBuildingOutlineSelected
        ? current.selectedStoreyId
        : null;
    const nextCamera = serializeDesignCamera(current.camera) ?? null;
    if (
      selectedParam === nextSelected &&
      storeyParam === nextStorey &&
      cameraParam === nextCamera
    ) {
      return;
    }
    setSearchParams(
      (params) => {
        if (nextSelected) {
          params.set(DESIGN_SELECTED_SEARCH_PARAM, nextSelected);
        } else {
          params.delete(DESIGN_SELECTED_SEARCH_PARAM);
        }

        if (nextStorey) {
          params.set(DESIGN_STOREY_SEARCH_PARAM, nextStorey);
        } else {
          params.delete(DESIGN_STOREY_SEARCH_PARAM);
        }

        if (nextCamera) params.set(DESIGN_CAMERA_SEARCH_PARAM, nextCamera);
        else params.delete(DESIGN_CAMERA_SEARCH_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [
    camera,
    cameraParam,
    hydratedProjectId,
    isBuildingOutlineSelected,
    projectId,
    selectedBuildingId,
    selectedParam,
    selectedStoreyId,
    setSearchParams,
    storeyParam,
  ]);
};
